import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/contexts/LanguageContext';
import { Loader2, ShieldAlert, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

type RequiredRole = 'super_admin' | 'admin' | 'manager' | 'any';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: RequiredRole;
}

export default function ProtectedRoute({ children, requiredRole = 'any' }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isApproved, role, isSuperAdmin, isAdminOrHigher, isManagerOrHigher, signOut } = useAuth();
  const { language } = useLanguage();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">
            {language === 'es' ? 'Cargando...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Check if user is approved
  if (!isApproved) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-chart-5/10 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-chart-5" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-4">
            {language === 'es' ? 'Cuenta Pendiente de Aprobación' : 'Account Pending Approval'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {language === 'es'
              ? 'Tu cuenta ha sido creada pero necesita ser aprobada por un Super Administrador antes de poder acceder al panel.'
              : 'Your account has been created but needs to be approved by a Super Administrator before you can access the panel.'}
          </p>
          <Button variant="outline" onClick={() => signOut()}>
            {language === 'es' ? 'Cerrar Sesión' : 'Sign Out'}
          </Button>
        </div>
      </div>
    );
  }

  // Check role requirements
  let hasRequiredRole = false;
  switch (requiredRole) {
    case 'super_admin':
      hasRequiredRole = isSuperAdmin;
      break;
    case 'admin':
      hasRequiredRole = isAdminOrHigher;
      break;
    case 'manager':
      hasRequiredRole = isManagerOrHigher;
      break;
    case 'any':
      hasRequiredRole = !!role;
      break;
  }

  if (!hasRequiredRole) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
            <ShieldAlert className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-4">
            {language === 'es' ? 'Acceso Denegado' : 'Access Denied'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {language === 'es'
              ? 'No tienes los permisos necesarios para acceder a esta sección.'
              : 'You do not have the required permissions to access this section.'}
          </p>
          <Button variant="outline" asChild>
            <a href="/admin">
              {language === 'es' ? 'Volver al Dashboard' : 'Back to Dashboard'}
            </a>
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
